<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import type {
    Project,
    ProjectsByStatus,
    ProjectStatus,
    CalendarEvent,
    DisplayFilter,
  } from '../lib/types';
  import { showProjects, showEvents } from '../lib/types';
  import {
    getShortMonthWithYear,
    getWeekKey,
    getWeekDisplayRange,
    categoryIcons,
    statusAccentColors,
    eventsForMonth,
    parseCalendarDate,
    progressStyle,
    progressComplete,
    sortEventsForDisplay,
    formatEventTimeShort,
  } from '../lib/utils';
  import { showProjectTooltip, hideProjectTooltip } from '../lib/stores/projectTooltip';
  import {
    draggedProject,
    openProjectRail,
    openProjectModalForCreate,
    openMonthRail,
    openEventsRailForMonth,
    openEventModalForEdit,
    openEventModal,
    requestProjectMove,
  } from '../lib/stores/ui';
  import { setupMaxVisible } from '../lib/useMaxVisible';
  import { showProjectCellPopover, hideProjectCellPopover } from '../lib/stores/projectCellPopover';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    visibleMonths: string[];
    projectsByMonth: Record<string, ProjectsByStatus>;
    events: CalendarEvent[];
    currentMonth: string;
    display?: DisplayFilter | null;
  }

  let { visibleMonths, projectsByMonth, events, currentMonth, display = null }: Props = $props();

  // Organize months into quarters (columns)
  // Q1: Jan, Feb, Mar | Q2: Apr, May, Jun | Q3: Jul, Aug, Sep | Q4: Oct, Nov, Dec
  let quarters = $derived([
    [visibleMonths[0], visibleMonths[1], visibleMonths[2]], // Q1
    [visibleMonths[3], visibleMonths[4], visibleMonths[5]], // Q2
    [visibleMonths[6], visibleMonths[7], visibleMonths[8]], // Q3
    [visibleMonths[9], visibleMonths[10], visibleMonths[11]], // Q4
  ]);

  const quarterLabels = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Dynamic max visible based on available height (2-column grid)
  const DEFAULT_VISIBLE = 4;

  let gridContainer: HTMLElement;
  let maxVisible = $state(DEFAULT_VISIBLE);

  onMount(() => {
    const contentEl = gridContainer?.querySelector('.month-content') as HTMLElement;
    if (!contentEl) return;

    const cleanup = setupMaxVisible(
      contentEl,
      {
        itemHeight: 21.5,
        gap: 2,
        containerPadding: 8,
        columns: 2,
        minVisible: 1,
      },
      (max) => {
        maxVisible = max;
      },
    );

    return cleanup;
  });

  // Status colors for chips

  function getAllProjects(monthKey: string): Project[] {
    const data = projectsByMonth[monthKey] || { planned: [], active: [], almost: [], done: [] };
    return [...(data.active || []), ...(data.planned || []), ...(data.done || [])];
  }

  function getMonthEvents(monthKey: string): CalendarEvent[] {
    return eventsForMonth(events, monthKey);
  }

  function formatEventDate(dateStr: string): string {
    const day = parseInt(dateStr.split('-')[2]);
    return String(day);
  }

  // Group events by week within a month
  interface WeekGroup {
    weekKey: string;
    label: string;
    events: CalendarEvent[];
  }

  function getEventsGroupedByWeek(monthKey: string): WeekGroup[] {
    const monthEvents = getMonthEvents(monthKey);
    if (monthEvents.length === 0) return [];

    const weekMap = new Map<string, CalendarEvent[]>();

    for (const event of monthEvents) {
      const eventDate = parseCalendarDate(event.date);
      const wk = getWeekKey(eventDate);
      if (!weekMap.has(wk)) {
        weekMap.set(wk, []);
      }
      weekMap.get(wk)!.push(event);
    }

    const sortedKeys = [...weekMap.keys()].sort();
    return sortedKeys.map((wk) => ({
      weekKey: wk,
      label: getWeekDisplayRange(wk),
      events: weekMap.get(wk)!,
    }));
  }

  function navigateToMonth(monthKey: string) {
    router.get(`/month/${monthKey}`, {}, { preserveScroll: true });
  }

  // Determine month state
  function getMonthState(monthKey: string): 'past' | 'current' | 'future' {
    if (monthKey === currentMonth) return 'current';
    if (monthKey < currentMonth) return 'past';
    return 'future';
  }

  // Add handlers
  function handleAddProject(monthKey: string) {
    openProjectModalForCreate({ schedulingState: 'scheduled', monthKey, status: 'planned' });
  }

  function handleAddEvent(monthKey: string) {
    openEventModal(`${monthKey}-01`);
  }

  // Double-click empty area to add
  function handleContentDoubleClick(e: MouseEvent, monthKey: string) {
    const target = e.target as HTMLElement;
    if (
      target.closest('.project-chip') ||
      target.closest('.event-chip') ||
      target.closest('.more-chip')
    )
      return;
    if (showEvents(display) && !showProjects(display)) {
      handleAddEvent(monthKey);
    } else {
      handleAddProject(monthKey);
    }
  }

  // Project cell popover handlers
  function handleMoreChipEnter(e: MouseEvent, monthKey: string, projects: Project[]) {
    showProjectCellPopover(
      projects,
      getShortMonthWithYear(monthKey),
      e.currentTarget as HTMLElement,
    );
  }

  function handleMoreChipLeave() {
    hideProjectCellPopover();
  }

  // Drag state
  let draggingProjectId = $state<string | null>(null);
  let dropTarget = $state<string | null>(null);

  function handleDragStart(e: DragEvent, project: Project) {
    hideProjectTooltip(true);
    draggingProjectId = project.id;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(project.id));
    }
  }

  function handleDragEnd() {
    draggingProjectId = null;
    draggedProject.set(null);
  }

  function handleDragOver(e: DragEvent, monthKey: string) {
    e.preventDefault();
    if ($draggedProject) {
      dropTarget = monthKey;
    }
  }

  function handleDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      dropTarget = null;
    }
  }

  function handleDrop(e: DragEvent, monthKey: string) {
    e.preventDefault();
    dropTarget = null;

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId) {
      requestProjectMove(projectId, $draggedProject, 'month', { monthKey, status: 'planned' });
    }
  }
</script>

<div class="grid-container" bind:this={gridContainer}>
  <div class="grid-content">
    <!-- Quarter columns -->
    <div class="quarters-grid">
      {#each quarters as quarterMonths, qIndex}
        <div class="quarter-column">
          <div class="quarter-header">
            <span class="quarter-label">{quarterLabels[qIndex]}</span>
          </div>

          <div class="quarter-months">
            {#each quarterMonths as monthKey (monthKey)}
              {@const state = getMonthState(monthKey)}
              {@const isCurrent = state === 'current'}
              {@const isPast = state === 'past'}
              {@const isFuture = state === 'future'}
              {@const projects = getAllProjects(monthKey)}
              {@const visibleProjects = projects.slice(0, maxVisible)}
              {@const hiddenCount = projects.length - maxVisible}
              {@const monthEvents = getMonthEvents(monthKey)}

              <div
                class="month-cell"
                class:past={isPast}
                class:current={isCurrent}
                class:future={isFuture}
                class:drop-target={dropTarget === monthKey}
                role="group"
                ondragover={(e) => handleDragOver(e, monthKey)}
                ondragleave={handleDragLeave}
                ondrop={(e) => handleDrop(e, monthKey)}
              >
                <div class="month-header-row">
                  <button
                    type="button"
                    class="month-header-btn"
                    onclick={() => navigateToMonth(monthKey)}
                  >
                    <span class="month-name">{getShortMonthWithYear(monthKey)}</span>
                    {#if isPast}
                      <span class="month-indicator past-indicator">✓</span>
                    {:else if isCurrent}
                      <span class="month-indicator current-indicator">★</span>
                    {:else}
                      <span class="month-indicator future-indicator">🎯</span>
                    {/if}
                  </button>
                  <button
                    type="button"
                    class="month-add-btn"
                    onclick={() =>
                      openProjectModalForCreate({
                        schedulingState: 'scheduled',
                        monthKey,
                        status: 'planned',
                      })}
                    title="Add project to {getShortMonthWithYear(monthKey)}"
                  >
                    +
                  </button>
                </div>

                <div
                  class="month-content"
                  role="group"
                  ondblclick={(e) => handleContentDoubleClick(e, monthKey)}
                >
                  {#if showProjects(display)}
                    <div class="project-chips">
                      {#each visibleProjects as project (project.id)}
                        <button
                          type="button"
                          class="project-chip {statusAccentColors[project.status]}"
                          style={progressStyle(project)}
                          data-progress-complete={progressComplete(project) || undefined}
                          class:dragging={draggingProjectId === project.id}
                          draggable={!$viewport.isMobile}
                          ondragstart={(e) => handleDragStart(e, project)}
                          ondragend={handleDragEnd}
                          onclick={() => {
                            hideProjectTooltip(true);
                            openProjectRail(project.id);
                          }}
                          onmouseenter={(e) =>
                            showProjectTooltip(project, e.currentTarget as HTMLElement)}
                          onmouseleave={() => hideProjectTooltip()}
                          title="{project.title} ({project.status}){project.multiWeek
                            ? ` — ${project.totalWeeks} weeks`
                            : ''}"
                        >
                          <span class="chip-category">{categoryIcons[project.category]}</span>
                          {#if project.isPriority}
                            <span class="chip-star">★</span>
                          {/if}
                          <span class="chip-title">{project.title}</span>
                          {#if project.multiWeek && project.totalWeeks}
                            <span class="chip-span">{project.totalWeeks}w</span>
                          {/if}
                        </button>
                      {/each}
                      {#if hiddenCount > 0}
                        <span
                          class="more-chip"
                          role="note"
                          onmouseenter={(e) => handleMoreChipEnter(e, monthKey, projects)}
                          onmouseleave={handleMoreChipLeave}
                        >
                          +{hiddenCount} more
                        </span>
                      {/if}
                      {#if projects.length === 0}
                        <span class="empty-month">
                          {isFuture ? 'No targets' : 'Empty'}
                        </span>
                      {/if}
                    </div>
                  {/if}
                  {#if showEvents(display)}
                    {@const weekGroups = getEventsGroupedByWeek(monthKey)}
                    <div class="event-chips">
                      {#if weekGroups.length > 0}
                        {#each weekGroups as group (group.weekKey)}
                          <div class="week-group">
                            <div class="week-group-label">{group.label}</div>
                            {#each group.events as event (event.id)}
                              {@const timeStr = formatEventTimeShort(event)}
                              <button
                                type="button"
                                class="event-chip event-{event.type}"
                                onclick={() => openEventModalForEdit(event)}
                                title="{timeStr ? `${timeStr} ` : ''}{event.title} ({event.date})"
                              >
                                <span class="event-date"
                                  >{formatEventDate(event.date)}{#if timeStr}
                                    · {timeStr}{/if}</span
                                >
                                <span class="chip-title">{event.title}</span>
                              </button>
                            {/each}
                          </div>
                        {/each}
                      {:else}
                        <span class="empty-month">No events</span>
                      {/if}
                    </div>
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/each}
    </div>
  </div>
</div>

<style>
  .grid-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.5rem;
    background: #f9fafb;
  }

  .grid-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.375rem;
    padding: 0.5rem;
    min-height: 0;
    overflow: hidden;
  }

  .quarters-grid {
    display: flex;
    gap: 0.375rem;
    flex: 1;
    min-height: 0;
  }

  .quarter-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 0.375rem;
  }

  .quarter-header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    background: #f3f4f6;
    border-radius: 0.25rem;
    flex-shrink: 0;
  }

  .quarter-label {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #6b7280;
    letter-spacing: 0.025em;
  }

  .quarter-months {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    flex: 1;
    min-height: 0;
  }

  .month-cell {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fafafa;
    border-radius: 0.25rem;
    overflow: hidden;
    transition: all 0.15s;
    min-height: 0;
  }

  .month-cell.past {
    background: #f5f5f5;
    box-shadow: inset 0 0 0 1px #e5e7eb;
  }

  .month-cell.current {
    background: #eff6ff;
    box-shadow: inset 0 0 0 1px #93c5fd;
  }

  .month-cell.future {
    background: #fdf9ff;
    box-shadow: inset 0 0 0 1px #e9d5ff;
  }

  .month-cell.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }

  .month-header-row {
    display: flex;
    align-items: stretch;
    flex-shrink: 0;
  }

  .month-header-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0.375rem;
    background: none;
    border: none;
    cursor: pointer;
    transition: background 0.15s;
    min-width: 0;
  }

  .month-header-btn:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .month-add-btn {
    width: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    color: #9ca3af;
    background: none;
    border: none;
    cursor: pointer;
    transition: all 0.15s;
    flex-shrink: 0;
  }

  .month-add-btn:hover {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
  }

  .month-cell.future .month-add-btn:hover {
    color: #8b5cf6;
    background: rgba(139, 92, 246, 0.1);
  }

  .month-name {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #374151;
    letter-spacing: -0.01em;
  }

  .month-indicator {
    font-size: 0.625rem;
  }

  .past-indicator {
    color: #d1d5db;
  }

  .current-indicator {
    color: #3b82f6;
  }

  .future-indicator {
    color: #a78bfa;
  }

  .month-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 0.25rem;
    overflow-y: auto;
    min-height: 0;
  }

  .project-chips {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.125rem;
    flex: 1;
    align-content: start;
  }

  .project-chip {
    display: flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.1875rem 0.25rem;
    border-radius: 0.1875rem;
    font-size: 0.5625rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    cursor: grab;
    text-align: left;
    transition: all 0.15s;
    min-width: 0;
  }

  .chip-category {
    font-size: 0.5rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .project-chip:hover {
    background: #f3f4f6;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .project-chip.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .chip-star {
    color: #facc15;
    font-size: 0.5rem;
    flex-shrink: 0;
  }

  .chip-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #374151;
    min-width: 0;
  }

  .chip-span {
    flex-shrink: 0;
    font-size: 0.5rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0 0.1875rem;
    border-radius: 0.125rem;
    letter-spacing: -0.02em;
  }

  .more-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.1875rem 0.25rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: 1px dashed #9ca3af;
    border-radius: 0.1875rem;
    cursor: default;
    text-align: center;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .more-chip:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  .project-chips > .more-chip,
  .project-chips > .empty-month {
    grid-column: 1 / -1;
  }

  .empty-month {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    font-size: 0.5625rem;
    color: #9ca3af;
    text-align: center;
  }

  /* Event chips */
  .event-chips {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .event-chips > .empty-month {
    grid-column: 1 / -1;
  }

  .week-group {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.0625rem;
  }

  .week-group-label {
    grid-column: 1 / -1;
    font-size: 0.4375rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.125rem 0.125rem 0;
    margin-top: 0.125rem;
  }

  .week-group:first-child .week-group-label {
    margin-top: 0;
  }

  .event-chip {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.1875rem 0.25rem;
    padding-left: 0.4375rem;
    border-radius: 0.1875rem;
    font-size: 0.5625rem;
    min-width: 0;
    overflow: hidden;
    background: #f3f4f6;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: opacity 0.1s;
  }

  .event-chip:hover {
    opacity: 0.8;
  }

  .event-chip::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #9ca3af;
    border-radius: 0.1875rem 0 0 0.1875rem;
  }

  .event-chip.event-appointment::before {
    background: #60a5fa;
  }
  .event-chip.event-travel::before {
    background: #a78bfa;
  }
  .event-chip.event-event::before {
    background: #4ade80;
  }
  .event-chip.event-deadline::before {
    background: #f87171;
  }

  .event-date {
    font-weight: 600;
    flex-shrink: 0;
    color: #6b7280;
  }
</style>
